import { defineQuiz } from "./_schema";
import type { Answers, ConcernKey, PersonaKey, Question } from "./_schema";
import { deriveQuizV7Profile } from "./quiz-v7-profile";

/**
 * quiz-v7 — concern-routed Mars Men quiz. NEW slug, distinct from live Ignite.
 * Branches on Q2 (`concern`) into 5 cohort paths, then converges on a shared
 * diagnostic tail. ~9 screens/user vs live Ignite's 14.
 *
 * Architecture: persona-routing kind. Reuses the 6-persona engine. Cohort
 * framing rides on `profile.cohort` (added by deriveQuizV7Profile, which
 * wraps deriveIgniteProfile without touching it).
 *
 * Classifier rule order (operator-confirmed 2026-05-29):
 * Performance -> TRT-alternative -> Skeptical-buyer -> Aging-men ->
 * sex-drive->trt-alternative -> everyday-man fallback.
 * glp1-user is intentionally unreachable (no GLP-1 screener in this quiz).
 */

const BRANCH_FLOWS: Record<ConcernKey, readonly string[]> = {
  energy:        ["energy-3pm", "energy-cost", "info-energy-validation", "when-started"],
  muscle:        ["body-composition", "strength-trend", "info-muscle-validation", "recovery"],
  "sex-drive":   ["libido-change", "morning-drive", "info-drive-validation", "when-started"],
  focus:         ["fog-frequency", "focus-cost", "info-focus-validation", "when-started"],
  "weight-loss": ["body-composition", "weight-where", "info-weight-validation", "processed-foods"],
};

const TAIL_FLOW = ["sleep-hours", "stress-symptoms", "info-analyzing"] as const;

function isConcernKey(v: unknown): v is ConcernKey {
  return typeof v === "string" && v in BRANCH_FLOWS;
}

function nextInList(list: readonly string[], current: string): string | null {
  const i = list.indexOf(current);
  if (i < 0) return null;
  return list[i + 1] ?? null;
}
const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40-49 years old" },
      { value: "30-39", label: "30-39 years old" },
      { value: "20-29", label: "20-29 years old" },
    ],
  },
  {
    id: "concern",
    type: "single",
    prompt: "What’s the one thing you’d fix about your masculine health first?",
    options: [
      { value: "energy", label: "Energy that lasts all day", emoji: "⚡" },
      { value: "muscle", label: "Muscle, strength & physique", emoji: "💪" },
      { value: "sex-drive", label: "Sex drive & performance", emoji: "🔥" },
      { value: "focus", label: "Mental focus & clarity", emoji: "🧠" },
      { value: "weight-loss", label: "Stubborn weight", emoji: "⚖️" },
    ],
  },
  // -- energy branch --
  {
    id: "energy-3pm",
    type: "single",
    prompt: "By 3pm, how does your energy compare to when you woke up?",
    options: [
      { value: "drained", label: "Completely drained, need caffeine or a nap" },
      { value: "afternoon-slump", label: "Noticeably lower, the slump hits hard" },
      { value: "slightly-tired", label: "Slightly tired, slower but functional" },
      { value: "same-energy", label: "Same energy, no crash" },
    ],
  },
  {
    id: "energy-cost",
    type: "single",
    prompt: "What does the afternoon crash cost you most?",
    options: [
      { value: "skip-gym", label: "I skip the gym I planned to hit" },
      { value: "short-fuse", label: "I’m short-tempered with people I love" },
      { value: "caffeine-crutch", label: "I run on caffeine just to function" },
      { value: "coasting", label: "I’m coasting through work I used to crush" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"The 3pm crash isn’t a willpower problem. As testosterone drops you lose the signal that runs daytime energy and drive, so you lean on caffeine and sugar to override it, while the stress hormones underneath keep pulling T down. We map where that’s hitting hardest.",
    },
  },
  {
    id: "info-energy-validation",
    type: "info",
    heading: "This isn’t just \"getting older.\"",
    body: [
      "The 3pm crash you described is one of the most common early signs of declining testosterone, and one of the most reversible. Of the 429,576 men who’ve used our T-revival system, the ones who came in for energy felt the first shift fastest.",
    ],
    image: {
      src: "/static/images/energy-couch.webp",
      alt: "Pushing yourself up off the couch through the afternoon crash",
    },
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did the crashes start showing up?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  // -- muscle branch --
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your build right now?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Soft with some muscle" },
      { value: "overweight", label: "Overweight / out of shape" },
    ],
  },
  {
    id: "strength-trend",
    type: "single",
    prompt: "How’s your strength trending lately?",
    options: [
      { value: "losing-ground", label: "Going backwards, lifts are dropping" },
      { value: "stuck", label: "Stuck, same numbers for months" },
      { value: "slow-gains", label: "Gaining, but slower than it should be" },
      { value: "progressing", label: "Progressing well" },
    ],
  },
  {
    id: "info-muscle-validation",
    type: "info",
    heading: "Effort was never your problem.",
    body: [
      "When strength stalls and recovery drags despite hard training, the bottleneck is almost always the anabolic signal, not your work ethic. Restore the signal and the same training starts paying off again.",
    ],
    image: {
      src: "/static/images/before-after.gif",
      alt: "Mars Men customers: before and after",
    },
  },
  {
    id: "recovery",
    type: "single",
    prompt: "How do you feel the day after a hard session?",
    options: [
      { value: "wrecked", label: "Wrecked, sore for days" },
      { value: "slow", label: "Slower to bounce back than I used to be" },
      { value: "normal", label: "About normal" },
      { value: "fast", label: "Fresh, I recover fast" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"Building muscle isn’t just training. It’s the anabolic signal testosterone sends afterward. When that signal weakens you train just as hard but recover slower and grow less. That gap is what we measure.",
    },
  },
  // -- sex-drive branch --
  {
    id: "libido-change",
    type: "single",
    prompt: "How has your sex drive changed over the past year?",
    options: [
      { value: "much-lower", label: "Much lower than it used to be" },
      { value: "somewhat-lower", label: "Somewhat lower" },
      { value: "inconsistent", label: "Comes and goes" },
      { value: "strong", label: "Still strong" },
    ],
  },
  {
    id: "morning-drive",
    type: "single",
    prompt: "How often do you wake up with strong morning drive?",
    options: [
      { value: "rarely", label: "Rarely or never" },
      { value: "sometimes", label: "A few times a month" },
      { value: "most-days", label: "Most mornings" },
      { value: "daily", label: "Almost every day" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"Morning drive is one of the most direct day-to-day readouts of overnight testosterone production. Loss of morning erections is one of the strongest single-symptom signals of low T, usually noticed before a man thinks to ask for bloodwork.",
    },
  },
  {
    id: "info-drive-validation",
    type: "info",
    heading: "You’re not broken. The signal is.",
    body: [
      "A fading drive is one of the body’s clearest signals that testosterone is dropping, and the marker most men report shifting first when it comes back. You’re tracking the right one.",
    ],
    image: {
      src: "/static/images/drive-bed.avif",
      alt: "A couple in bed on opposite sides, drifting instead of connecting",
    },
  },
  // -- focus branch --
  {
    id: "fog-frequency",
    type: "single",
    prompt: "How often does brain fog or mental fatigue hit you?",
    options: [
      { value: "daily", label: "Daily, it’s my normal now" },
      { value: "often", label: "Several times a week" },
      { value: "sometimes", label: "Now and then" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "focus-cost",
    type: "single",
    prompt: "Where does the fog cost you most?",
    options: [
      { value: "work", label: "Focus at work, deadlines slip" },
      { value: "memory", label: "Memory and word recall" },
      { value: "motivation", label: "Drive and motivation to start things" },
      { value: "mood", label: "Mood and patience" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"The brain runs on testosterone too. It drives dopamine, motivation and mental sharpness. When levels fall, the fog, the lost words and the flat motivation show up together. They share one upstream cause.",
    },
  },
  {
    id: "info-focus-validation",
    type: "info",
    heading: "The fog has a cause you can fix.",
    body: [
      "Mental fog, lost words, and flat motivation aren’t separate problems. They’re downstream of the same hormonal signal that runs focus and drive. Fix the upstream cause and they tend to lift together.",
    ],
    // Operator-supplied 2026-06-11. The Mars Men canister in frame is the
    // focus branch's first product appearance, chosen deliberately (same
    // role as the before-after.gif on the muscle/weight bridges).
    image: {
      src: "/static/images/focus-desk.webp",
      alt: "Locked in at the laptop, mid-morning, head clear",
    },
  },
  // -- weight-loss branch --
  {
    id: "weight-where",
    type: "single",
    prompt: "Where does the weight sit most stubbornly?",
    options: [
      { value: "belly", label: "Belly and midsection" },
      { value: "love-handles", label: "Love handles and lower back" },
      { value: "all-over", label: "Spread all over" },
      { value: "chest", label: "Chest and face" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"Belly fat isn’t just stored energy. It’s hormonally active. It converts testosterone into estrogen, which tells your body to store even more fat. That’s the loop most diets never break, because they never touch the hormone driving it.",
    },
  },
  {
    id: "info-weight-validation",
    type: "info",
    heading: "The diet was fighting your hormones.",
    body: [
      "Stubborn belly fat produces estrogen, which tells your body to store even more fat, a loop willpower can’t out-muscle. The men who finally broke it didn’t diet harder; they fixed the hormone driving the storage.",
    ],
    image: {
      src: "/static/images/before-after.gif",
      alt: "Mars Men customers: before and after",
    },
  },
  {
    id: "processed-foods",
    type: "single",
    prompt: "How often do processed foods show up in your week?",
    options: [
      { value: "daily", label: "Daily, most meals" },
      { value: "several-week", label: "Several times a week" },
      { value: "occasionally", label: "Occasionally, mostly whole foods" },
      { value: "rarely", label: "Rarely, I avoid them" },
    ],
  },
  // -- shared diagnostic tail --
  {
    id: "sleep-hours",
    type: "single",
    prompt: "How many hours of quality sleep do you get on a typical night?",
    options: [
      { value: "lt-5", label: "Less than 5 hours" },
      { value: "5-6", label: "5-6 hours" },
      { value: "7-8", label: "7-8 hours" },
      { value: "gt-8", label: "More than 8 hours" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"Most of your testosterone is produced during deep sleep. A short or broken window caps how much your body can make, no matter what else you do right.",
    },
  },
  {
    id: "stress-symptoms",
    type: "single",
    prompt: "How often do stress symptoms (wired, foggy, cranky, or burned out) hit you through the day?",
    options: [
      { value: "constant", label: "Constant, this is my normal now" },
      { value: "most-days", label: "Most days, it builds by the afternoon" },
      { value: "some-days", label: "Some days, it comes and goes" },
      { value: "rarely", label: "Rarely, I stay pretty even" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body:"Stress raises cortisol, and cortisol is testosterone's direct competitor. When one goes up, the other comes down. Chronic stress keeps your body in defense mode instead of rebuild mode.",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    heading: "Building your testosterone profile…",
    body: [
      "We're cross-referencing your answers against the patterns we've seen in hundreds of thousands of men to pinpoint what's suppressing your testosterone, and what reverses it.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

/**
 * Single source of truth for cohort-specific copy on the Result, Projection,
 * and Offer surfaces. Each entry is read by the corresponding component when
 * `profile.cohort` is set; when `cohort` is absent (live Ignite path), the
 * components fall through to today's default copy byte-identically.
 *
 * Strings are verbatim from the design source. Audit-locked — do not edit
 * without re-running /grill-with-docs.
 */
export const COHORT_OVERLAYS: Record<
  ConcernKey,
  {
    eyebrow: string;
    resultSubline: string;
    projectionSubhead: string;
    offerHeadline: string;
    utmTerm: ConcernKey;
  }
> = {
  energy: {
    eyebrow: "YOUR ENERGY PROFILE",
    resultSubline:
      "This is why the afternoon crash kicks in, and why caffeine stops being enough.",
    projectionSubhead:
      "Where your energy line is heading vs. where it can return to.",
    offerHeadline: "Energy comes back first.",
    utmTerm: "energy",
  },
  muscle: {
    eyebrow: "YOUR STRENGTH PROFILE",
    resultSubline: "This is what's making the same training pay off less.",
    projectionSubhead:
      "Where your anabolic signal is heading vs. where it can return to.",
    offerHeadline: "Same training. Different return.",
    utmTerm: "muscle",
  },
  "sex-drive": {
    eyebrow: "YOUR DRIVE PROFILE",
    resultSubline:
      "This is the upstream cause of the slip in drive you described.",
    projectionSubhead:
      "Where your drive is heading vs. where it can return to.",
    offerHeadline: "Drive returns first. The rest follows.",
    utmTerm: "sex-drive",
  },
  focus: {
    eyebrow: "YOUR FOCUS PROFILE",
    resultSubline:
      "This is what's behind the fog, the lost words, and the flat motivation.",
    projectionSubhead:
      "Where your sharpness is heading vs. where it can return to.",
    offerHeadline: "The fog has a fix.",
    utmTerm: "focus",
  },
  "weight-loss": {
    eyebrow: "YOUR METABOLIC PROFILE",
    resultSubline:
      "This is the hormone running the storage loop your diet keeps fighting.",
    projectionSubhead:
      "Where your metabolic signal is heading vs. where it can return to.",
    offerHeadline: "Break the storage loop, not the willpower.",
    utmTerm: "weight-loss",
  },
};

export const quizV7Quiz = defineQuiz({
  id: "quiz-v7",
  slug: "quiz-v7",
  meta: {
    title: "What’s Suppressing Your Testosterone",
    introHeadline: "Find Out What’s Really Suppressing Your Testosterone",
    introBody:
      "This 2-minute assessment analyzes your symptoms, lifestyle, and risk factor in order to find a solution.",
    testimonial: {
      // Mirror of quiz-v6 (ignite) entry proof per operator: quiz-benefit,
      // reveal-safe. variantTag dropped since quiz-v7 has no experiment block.
      quote:
        "Took maybe a minute. Let’s just say me and my wife are VERY happy I took this quiz.",
      author: "ADAM, Verified Customer",
    },
  },
  questions,
  branching(answers: Answers, currentQuestionId: string): string | null {
    if (currentQuestionId === "age") return "concern";

    const concern = answers["concern"];
    if (!isConcernKey(concern)) return null;
    const branchFlow = BRANCH_FLOWS[concern];

    if (currentQuestionId === "concern") return branchFlow[0] ?? TAIL_FLOW[0];

    if (branchFlow.includes(currentQuestionId)) {
      const next = nextInList(branchFlow, currentQuestionId);
      return next ?? TAIL_FLOW[0];
    }

    if ((TAIL_FLOW as readonly string[]).includes(currentQuestionId)) {
      return nextInList(TAIL_FLOW, currentQuestionId);
    }

    return null;
  },
  classifier(answers: Answers): PersonaKey {
    const concern = answers["concern"];
    const energy3pm = answers["energy-3pm"];
    const whenStarted = answers["when-started"];
    const bodyComp = answers["body-composition"];
    const sleep = answers["sleep-hours"];
    const age = answers["age"];

    // 1. Performance: lean / muscular + sleeps well + muscle/focus concern.
    if (
      bodyComp === "lean-muscular" &&
      (sleep === "7-8" || sleep === "gt-8") &&
      (concern === "muscle" || concern === "focus")
    ) {
      return "performance";
    }

    // 2. TRT-alternative: long-running + drained + 50-plus.
    // (quiz-v7 doesn't collect family-history, so we gate on age alone vs Ignite's
    // family-history-OR-50-plus disjunction.)
    if (
      whenStarted === "over-year" &&
      energy3pm === "drained" &&
      age === "50-plus"
    ) {
      return "trt-alternative";
    }

    // 3. Skeptical buyer: not-sure onset OR weight-loss concern.
    if (whenStarted === "not-sure" || concern === "weight-loss") {
      return "skeptical-buyer";
    }

    // 4. Aging men: 40+ with body softening, sleep issues, or overweight.
    if (
      (age === "50-plus" || age === "40-49") &&
      (bodyComp === "soft-some-muscle" || bodyComp === "overweight" || sleep === "lt-5")
    ) {
      return "aging-men";
    }

    // 5. Concern-aware fallback (operator-confirmed 2026-05-29).
    if (concern === "sex-drive") return "trt-alternative";

    return "everyday-man";
  },
  deriveProfile: deriveQuizV7Profile,
});
